/**
 * Cart-wide early_access quantity limit for employee customers.
 *
 * Employees may hold at most `limit` (5) paid early_access units across the cart.
 * Gift-with-purchase lines for the same SKU use `_gwp` (not `_early_access`) and are not counted.
 *
 * The cart count is rendered into #early-access-config by Liquid and kept current here on every
 * cart change, so all guards are synchronous — the PDP add is blocked before the theme's
 * ProductForm submit handler can fire (an async check would let the add slip through).
 */
(function () {
  'use strict';

  const CONFIG_ID = 'early-access-config';

  function readConfig() {
    const el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (_) {
      return null;
    }
  }

  function writeConfig(config) {
    const el = document.getElementById(CONFIG_ID);
    if (el && config) el.textContent = JSON.stringify(config);
  }

  function countEarlyAccess(cart) {
    let total = 0;
    for (const item of cart?.items || []) {
      if (item.properties?._early_access === 'true') total += item.quantity;
    }
    return total;
  }

  function remainingCapacity(config) {
    const current = Number(config?.cartEarlyAccessQuantity) || 0;
    const limit = Number(config?.limit) || 5;
    return Math.max(0, limit - current);
  }

  function isEarlyAccessAddForm(form) {
    if (!form?.action || !String(form.action).includes('/cart/add')) return false;
    const input = form.querySelector('[name="properties[_early_access]"]');
    return Boolean(input && input.value === 'true');
  }

  function addQtyForForm(form) {
    return parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10) || 1;
  }

  function wouldExceedLimit(config, addQty) {
    if (!config?.isEmployee) return false;
    return addQty > remainingCapacity(config);
  }

  /* ── PDP button + warning ──────────────────────────────────────── */

  function syncPdpForms(config) {
    const atLimit = Boolean(config?.isEmployee) && remainingCapacity(config) === 0;

    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (!isEarlyAccessAddForm(form)) return;

      const buttons = Array.from(form.querySelectorAll('[type="submit"]'));
      /* Sticky add-to-cart submits this form via the `form` attribute from outside it. */
      if (form.id) {
        buttons.push(...document.querySelectorAll(`[type="submit"][form="${form.id}"]`));
      }
      buttons.forEach((button) => {
        button.disabled = atLimit;
        button.toggleAttribute('aria-disabled', atLimit);
      });

      const notice = form.querySelector('[data-early-access-limit-notice]');
      if (notice) notice.hidden = !atLimit;
    });
  }

  /* ── Cart line quantity guard (employee early_access lines) ─────── */

  function showLineItemError(lineItem, message) {
    const container = lineItem?.querySelector('.line-item__error-container');
    if (!container) return;
    container.innerHTML = '';
    const errorSvg = `<svg width="13" viewBox="0 0 13 13"><circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="2"></circle><circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"></circle><path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"></path><path fill="#fff" stroke="#EB001B" stroke-width=".7" d="M5.874 3.178h-.359l.01.359.1 4.044.008.341h1.736l.008-.341.1-4.044.01-.359H5.873Zm.627 6.833c.56 0 1.03-.432 1.03-1.006s-.47-1.006-1.03-1.006-1.031.432-1.031 1.006.47 1.006 1.03 1.006Z"></path></svg>`;
    container.insertAdjacentHTML(
      'afterbegin',
      `<p class="h-stack gap-1.5 text-sm text-error" role="alert">${errorSvg} ${message}</p>`
    );
  }

  function clearLineItemError(lineItem) {
    const container = lineItem?.querySelector('.line-item__error-container');
    if (container) container.innerHTML = '';
  }

  function syncCartQuantitySelectors(config) {
    if (!config?.isEmployee) return;

    document.querySelectorAll('[data-early-access-line="true"] input[data-line-key]').forEach((input) => {
      const maxQty = parseInt(input.getAttribute('max'), 10);
      if (!Number.isFinite(maxQty)) return;

      const selector = input.closest('quantity-selector');
      const current = parseInt(input.value, 10);
      const plus = selector?.querySelector('.quantity-selector__button:last-of-type');
      if (plus) plus.disabled = !Number.isFinite(current) || current >= maxQty;
    });
  }

  function clampCartLineQuantity(input, config) {
    const lineItem = input.closest('[data-early-access-line="true"]');
    if (!lineItem || !config?.isEmployee) return false;

    const maxQty = parseInt(input.getAttribute('max'), 10);
    if (!Number.isFinite(maxQty)) return false;

    const targetQty = parseInt(input.value, 10);
    if (!Number.isFinite(targetQty) || targetQty <= maxQty) {
      clearLineItemError(lineItem);
      return false;
    }

    input.value = String(maxQty);
    showLineItemError(lineItem, config.limitMessage);
    return true;
  }

  /* ── State sync ────────────────────────────────────────────────── */

  function syncUi(config) {
    if (!config) return;
    syncPdpForms(config);
    syncCartQuantitySelectors(config);
  }

  function refreshFromCart(cart) {
    const config = readConfig();
    if (!config || !cart) return;
    config.cartEarlyAccessQuantity = countEarlyAccess(cart);
    writeConfig(config);
    syncUi(config);
  }

  /* ── Synchronous PDP add guard ─────────────────────────────────── */

  function blockEarlyAccessAdd(form) {
    if (!isEarlyAccessAddForm(form)) return false;
    const config = readConfig();
    if (!config?.isEmployee) return false;
    if (!wouldExceedLimit(config, addQtyForForm(form))) return false;
    syncPdpForms(config);
    return true;
  }

  document.addEventListener(
    'click',
    (event) => {
      const submitButton = event.target.closest('form[action*="/cart/add"] [type="submit"]');
      if (!submitButton) return;
      if (!blockEarlyAccessAdd(submitButton.closest('form'))) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (!blockEarlyAccessAdd(event.target)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    },
    true
  );

  /* ── Cart line "+" / manual quantity guard ─────────────────────── */

  document.addEventListener(
    'click',
    (event) => {
      const plusButton = event.target.closest('[data-early-access-line="true"] .quantity-selector__button:last-of-type');
      if (!plusButton || plusButton.disabled) return;

      const config = readConfig();
      if (!config?.isEmployee) return;

      const input = plusButton.closest('quantity-selector')?.querySelector('input[data-line-key]');
      if (!input) return;

      const maxQty = parseInt(input.getAttribute('max'), 10);
      const current = parseInt(input.value, 10);
      if (!Number.isFinite(maxQty) || !Number.isFinite(current) || current < maxQty) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showLineItemError(input.closest('[data-early-access-line="true"]'), config.limitMessage);
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!input?.matches?.('input[data-line-key]')) return;

      const config = readConfig();
      if (!config?.isEmployee) return;
      if (!input.closest('[data-early-access-line="true"]')) return;

      if (clampCartLineQuantity(input, config)) {
        event.stopImmediatePropagation();
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    },
    true
  );

  /* ── Keep cached count + UI in sync with the cart ──────────────── */

  document.addEventListener('cart:change', (event) => {
    if (event.detail?.cart) refreshFromCart(event.detail.cart);
  });

  document.addEventListener('cart-drawer:refreshed', () => syncUi(readConfig()));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncUi(readConfig()));
  } else {
    syncUi(readConfig());
  }
})();
